import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Alert, AlertDescription } from './alert';
import { AlertTriangle, CheckCircle, DollarSign, Calendar, Building } from 'lucide-react';
import { ReembolsoService, Reembolso, ComprovantePix } from '@/services/reembolsoService';

interface ModalProcessarReembolsoPixProps {
  isOpen: boolean;
  onClose: () => void;
  reembolso: Reembolso | null;
  onSuccess: () => void;
  userName?: string;
}

const ModalProcessarReembolsoPix: React.FC<ModalProcessarReembolsoPixProps> = ({
  isOpen,
  onClose,
  reembolso,
  onSuccess,
  userName
}) => {
  const [loading, setLoading] = useState(false);
  const [erros, setErros] = useState<string[]>([]);
  
  // Estados do comprovante PIX
  const [dataTransferencia, setDataTransferencia] = useState('');
  const [bancoOrigem, setBancoOrigem] = useState('');
  const [valorTransferido, setValorTransferido] = useState('');
  const [codigoOperacao, setCodigoOperacao] = useState('');
  const [observacoesAdmin, setObservacoesAdmin] = useState('');

  const validarFormulario = (): string[] => {
    const novosErros: string[] = [];

    if (!dataTransferencia) {
      novosErros.push('Data da transferência é obrigatória');
    }

    if (!bancoOrigem.trim()) {
      novosErros.push('Banco de origem é obrigatório');
    }

    const valorNum = parseFloat(valorTransferido);
    if (!valorTransferido || valorNum <= 0) {
      novosErros.push('Valor transferido deve ser maior que zero');
    }
    const valorReembolso = typeof reembolso?.valor === 'string' 
      ? parseFloat(reembolso.valor) 
      : (reembolso?.valor || 0);
    if (Math.abs(valorNum - valorReembolso) > 0.01) {
      novosErros.push(`Valor transferido deve ser igual ao valor do reembolso (${ReembolsoService.formatarValor(valorReembolso)})`);
    }

    if (!codigoOperacao.trim()) {
      novosErros.push('Código da operação é obrigatório');
    }

    return novosErros;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reembolso) return;
    
    const novosErros = validarFormulario();
    setErros(novosErros);
    
    if (novosErros.length > 0) return;

    try {
      setLoading(true);

      const comprovantePix: ComprovantePix = {
        id_transacao_pix: codigoOperacao, // Mapear codigo_operacao para id_transacao_pix
        data_transferencia: dataTransferencia,
        banco_origem: bancoOrigem,
        valor_transferido: parseFloat(valorTransferido),
        observacoes_admin: observacoesAdmin || undefined
      };

      await ReembolsoService.processarComComprovante(reembolso.id, {
        comprovante_pix: comprovantePix,
        observacao_admin: observacoesAdmin
      });

      onSuccess();
      handleClose();
    } catch (error: unknown) {
      const err = error as Error;
      setErros([err.message || 'Erro ao processar reembolso']);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDataTransferencia('');
    setBancoOrigem('');
    setValorTransferido('');
    setCodigoOperacao('');
    setObservacoesAdmin('');
    setErros([]);
    onClose();
  };

  const formatarDataHoje = () => {
    const hoje = new Date();
    return hoje.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
  };

  // Auto-preencher valor e data
  React.useEffect(() => {
    if (reembolso && isOpen) {
      const valorNumerico = typeof reembolso.valor === 'string' 
        ? parseFloat(reembolso.valor) 
        : reembolso.valor;
      setValorTransferido(valorNumerico.toFixed(2));
      if (!dataTransferencia) {
        setDataTransferencia(formatarDataHoje());
      }
    }
  }, [reembolso, isOpen, dataTransferencia]);

  if (!reembolso) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <CheckCircle className="w-6 h-6 text-green-600" />
            Processar Reembolso PIX
          </DialogTitle>
        </DialogHeader>

        {/* Info do Reembolso */}
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">Dados do Reembolso</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-blue-700 font-medium">Cliente:</span>
              <p className="text-blue-900">{userName || <span className="text-gray-400">Carregando nome...</span>}</p>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Valor:</span>
              <p className="text-blue-900 font-semibold">{ReembolsoService.formatarValor(reembolso.valor)}</p>
            </div>
            <div>
              <span className="text-blue-700 font-medium">CPF Chave PIX:</span>
              <p className="text-blue-900">{ReembolsoService.formatarCPF(reembolso.cpf_chave_pix)}</p>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Motivo:</span>
              <p className="text-blue-900">{reembolso.motivo}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados da Transferência PIX */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Dados da Transferência PIX
            </h3>

            {/* Código da Operação */}
            <div className="space-y-2">
              <Label htmlFor="codigo-operacao" className="text-sm font-medium">
                Código da Operação *
              </Label>
              <Input
                id="codigo-operacao"
                value={codigoOperacao}
                onChange={(e) => setCodigoOperacao(e.target.value)}
                placeholder="Ex: 123456789, COD123, OP456789"
                className="font-mono text-sm"
                maxLength={50}
              />
              <p className="text-xs text-gray-500">
                Código de identificação da operação bancária
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Data Transferência */}
              <div className="space-y-2">
                <Label htmlFor="data-transferencia" className="text-sm font-medium">
                  Data/Hora da Transferência *
                </Label>
                <Input
                  id="data-transferencia"
                  type="datetime-local"
                  value={dataTransferencia}
                  onChange={(e) => setDataTransferencia(e.target.value)}
                />
              </div>

              {/* Valor Transferido */}
              <div className="space-y-2">
                <Label htmlFor="valor-transferido" className="text-sm font-medium">
                  Valor Transferido (R$) *
                </Label>
                <Input
                  id="valor-transferido"
                  type="number"
                  step="0.01"
                  value={valorTransferido}
                  onChange={(e) => setValorTransferido(e.target.value)}
                  className="text-lg font-semibold"
                />
                <p className="text-xs text-gray-500">
                  Deve ser exatamente {ReembolsoService.formatarValor(reembolso.valor)}
                </p>
              </div>
            </div>

            {/* Banco de Origem */}
            <div className="space-y-2">
              <Label htmlFor="banco-origem" className="text-sm font-medium">
                Banco de Origem *
              </Label>
              <Input
                id="banco-origem"
                value={bancoOrigem}
                onChange={(e) => setBancoOrigem(e.target.value)}
                placeholder="Ex: Banco do Brasil, Itaú, Nubank..."
                maxLength={100}
              />
              <p className="text-xs text-gray-500">
                Banco de onde foi feita a transferência
              </p>
            </div>
          </div>

          {/* Observações Admin */}
          <div className="space-y-2">
            <Label htmlFor="observacoes" className="text-sm font-medium">
              Observações da Operação
            </Label>
            <textarea
              id="observacoes"
              value={observacoesAdmin}
              onChange={(e) => setObservacoesAdmin(e.target.value)}
              placeholder="Observações sobre o processamento do reembolso..."
              className="w-full p-3 border border-gray-300 rounded-md resize-none h-20"
              maxLength={300}
            />
            <p className="text-xs text-gray-500">
              {observacoesAdmin.length}/300 caracteres
            </p>
          </div>

          {/* Alerta de Confirmação */}
          <Alert className="border-orange-200 bg-orange-50">
            <AlertTriangle className="w-4 h-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <strong>ATENÇÃO:</strong> Ao confirmar, o reembolso será marcado como "PROCESSADO" 
              e o cliente será notificado. Esta ação não pode ser desfeita.
            </AlertDescription>
          </Alert>

          {/* Erros */}
          {erros.length > 0 && (
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <ul className="list-disc list-inside space-y-1">
                  {erros.map((erro, index) => (
                    <li key={index}>{erro}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Botões */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                'Processando...'
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar PIX Processado
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ModalProcessarReembolsoPix; 